import { useEffect, useState } from 'react';
import { formatDateDDMMYYYY } from '../utils/format';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface ResidentAccount {
  id: string;
  username: string;
  role: string;
  password_plain?: string | null;
  room_number?: string | null;
  last_login_at?: string | null;
  created_at?: string;
}

interface EditingPassword {
  userId: string;
  newPassword: string;
}

// Helper function to determine building type from room number
const getBuildingType = (roomNumber: string | null | undefined): 'gajah4' | 'gajah3' | 'anduring' | 'limau_manis' => {
  if (!roomNumber) return 'gajah4';
  if (roomNumber.startsWith('G3-')) return 'gajah3';
  if (roomNumber.startsWith('AND-')) return 'anduring';
  if (roomNumber.startsWith('LM-')) return 'limau_manis';
  return 'gajah4';
};

export function ResidentAccountManager({ visible, onClose }: { visible: boolean; onClose: () => void }) {
  const [residents, setResidents] = useState<ResidentAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof ResidentAccount>('room_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [editingPassword, setEditingPassword] = useState<EditingPassword | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!visible) return;
    void loadResidents();
  }, [visible]);

  const loadResidents = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!isSupabaseConfigured) throw new Error('Supabase belum dikonfigurasi');
      
      // Retrieve all resident accounts
      const { data: accounts, error } = await supabase
        .from('app_users')
        .select('id, username, role, password_plain, room_number, last_login_at, created_at')
        .eq('role', 'resident');
        
      if (error) throw error;
      
      // Get room info to enhance data with building_type
      const { data: roomsData, error: roomsError } = await supabase
        .from('rooms')
        .select('number, building_type');
        
      if (roomsError) throw roomsError;
      
      // Create a lookup map for room building types
      const roomBuildingMap = (roomsData || []).reduce((map: Record<string, string>, room: any) => {
        map[room.number] = room.building_type;
        return map;
      }, {});
      
      // Set the data
      setResidents((accounts || []) as ResidentAccount[]);
    } catch (e: any) {
      setError(e.message || 'Gagal memuat data akun penghuni');
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (userId: string, newPassword: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!newPassword.trim()) {
        throw new Error('Password tidak boleh kosong');
      }

      const { error } = await supabase
        .from('app_users')
        .update({ password_plain: newPassword })
        .eq('id', userId);
      
      if (error) throw error;
      
      // Log the action
      await logAction('update_resident_password', 'success', { userId, username: residents.find(r => r.id === userId)?.username });
      
      setEditingPassword(null);
      await loadResidents();
    } catch (e: any) {
      setError(e.message || 'Gagal mengubah password');
      await logAction('update_resident_password', 'error', { message: e.message });
    } finally {
      setLoading(false);
    }
  };

  const logAction = async (action: string, result: string, details?: any) => {
    try {
      await supabase.from('audit_logs').insert({
        username: 'yudha',
        action,
        result,
        details
      });
    } catch {}
  };

  const handleSort = (field: keyof ResidentAccount) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Helper function for sorting room numbers of different formats
  const getSortingValue = (roomNumber: string | null | undefined): number => {
    if (!roomNumber) return 999999;
    
    // For standard numeric room numbers (Gajah 4)
    if (/^[0-9]+$/.test(roomNumber)) {
      return parseInt(roomNumber);
    }
    
    // For G3-XX format
    if (/^G3-[0-9]+$/.test(roomNumber)) {
      const num = parseInt(roomNumber.replace('G3-', ''));
      return num + 1000; // Offset for Gajah 3
    }
    
    // For AND-XX format
    if (/^AND-[0-9]+$/.test(roomNumber)) {
      const num = parseInt(roomNumber.replace('AND-', ''));
      return num + 2000; // Offset for Anduring
    }
    
    // For LM-XX format
    if (/^LM-[0-9]+$/.test(roomNumber)) {
      const num = parseInt(roomNumber.replace('LM-', ''));
      return num + 3000; // Offset for Limau Manis
    }
    
    return 999999; // Default for unrecognized formats
  };

  const filteredAndSortedResidents = residents
    .filter(resident => 
      resident.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (resident.room_number && resident.room_number.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      
      if (sortField === 'room_number') {
        // Special sorting for room numbers using building-aware algorithm
        const aSort = getSortingValue(a.room_number);
        const bSort = getSortingValue(b.room_number);
        return sortDirection === 'asc' ? aSort - bSort : bSort - aSort;
      }
      
      const result = aVal.toString().localeCompare(bVal.toString());
      return sortDirection === 'asc' ? result : -result;
    });

  // Separate residents by building type
  const gajah4Residents = filteredAndSortedResidents.filter(resident => getBuildingType(resident.room_number) === 'gajah4');
  const gajah3Residents = filteredAndSortedResidents.filter(resident => getBuildingType(resident.room_number) === 'gajah3');
  const anduringResidents = filteredAndSortedResidents.filter(resident => getBuildingType(resident.room_number) === 'anduring');
  const limauManisResidents = filteredAndSortedResidents.filter(resident => getBuildingType(resident.room_number) === 'limau_manis');

  const SortIcon = ({ field }: { field: keyof ResidentAccount }) => {
    if (sortField !== field) return <span className="text-gray-400">↕️</span>;
    return <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const renderResidentTable = (residentsData: ResidentAccount[], buildingTitle: string) => (
    <div className="mb-8">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">{buildingTitle}</h3>
      
      {/* Building Stats */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{residentsData.length}</div>
          <div className="text-sm text-blue-600 dark:text-blue-400">Total Akun</div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg">
          <div className="text-xl font-bold text-green-600 dark:text-green-400">
            {residentsData.filter(r => r.last_login_at).length}
          </div>
          <div className="text-sm text-green-600 dark:text-green-400">Pernah Login</div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/30 p-3 rounded-lg">
          <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
            {residentsData.filter(r => !r.last_login_at).length}
          </div>
          <div className="text-sm text-orange-600 dark:text-orange-400">Belum Login</div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="text-left p-3 font-medium">
                <button 
                  onClick={() => handleSort('room_number')}
                  className="flex items-center gap-1 hover:text-blue-600"
                >
                  No. Kamar <SortIcon field="room_number" />
                </button>
              </th>
              <th className="text-left p-3 font-medium">
                <button 
                  onClick={() => handleSort('username')}
                  className="flex items-center gap-1 hover:text-blue-600"
                >
                  Username <SortIcon field="username" />
                </button>
              </th>
              <th className="text-left p-3 font-medium">Password</th>
              <th className="text-left p-3 font-medium">
                <button 
                  onClick={() => handleSort('last_login_at')}
                  className="flex items-center gap-1 hover:text-blue-600"
                >
                  Last Login <SortIcon field="last_login_at" />
                </button>
              </th>
              <th className="text-left p-3 font-medium">
                <button 
                  onClick={() => handleSort('created_at')}
                  className="flex items-center gap-1 hover:text-blue-600"
                >
                  Dibuat <SortIcon field="created_at" />
                </button>
              </th>
              <th className="text-left p-3 font-medium">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {residentsData.length === 0 ? (
              <tr>
                <td className="p-3 text-center text-gray-500" colSpan={6}>
                  Tidak ada data akun penghuni untuk {buildingTitle}
                </td>
              </tr>
            ) : (
              residentsData.map((resident) => (
                <tr key={resident.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="p-3 font-medium">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                      {resident.room_number || '-'}
                    </span>
                  </td>
                  <td className="p-3">{resident.username}</td>
                  <td className="p-3">
                    {editingPassword?.userId === resident.id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editingPassword.newPassword}
                          onChange={(e) => setEditingPassword({
                            ...editingPassword,
                            newPassword: e.target.value
                          })}
                          className="px-2 py-1 border rounded text-sm bg-white dark:bg-gray-800"
                          placeholder="Password baru"
                        />
                        <button
                          onClick={() => updatePassword(resident.id, editingPassword.newPassword)}
                          disabled={loading}
                          className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Simpan
                        </button>
                        <button
                          onClick={() => setEditingPassword(null)}
                          className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                          {resident.password_plain || 'Tidak ada'}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {resident.last_login_at ? (
                      <span className="text-green-600 dark:text-green-400">
                        {formatDateDDMMYYYY(resident.last_login_at)}
                      </span>
                    ) : (
                      <span className="text-gray-400">Belum pernah</span>
                    )}
                  </td>
                  <td className="p-3 text-gray-600 dark:text-gray-400">
                    {resident.created_at ? formatDateDDMMYYYY(resident.created_at) : '-'}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => setEditingPassword({
                        userId: resident.id,
                        newPassword: resident.password_plain || ''
                      })}
                      disabled={loading || editingPassword?.userId === resident.id}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-6xl p-6 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Manajemen Akun Penghuni Kos</h2>
          <button 
            onClick={onClose} 
            className="px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700"
          >
            Tutup
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Cari berdasarkan username atau nomor kamar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-3 border rounded-lg bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
          />
        </div>

        {/* Overall Stats */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{residents.length}</div>
            <div className="text-sm text-blue-600 dark:text-blue-400">Total Akun Penghuni</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {residents.filter(r => r.last_login_at).length}
            </div>
            <div className="text-sm text-green-600 dark:text-green-400">Pernah Login</div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {residents.filter(r => !r.last_login_at).length}
            </div>
            <div className="text-sm text-orange-600 dark:text-orange-400">Belum Pernah Login</div>
          </div>
        </div>

        {/* Separated Tables by Building */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2">Memuat data...</span>
          </div>
        ) : (
          <>
            {/* Tabs for different building types */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
                <li className="mr-2">
                  <button 
                    className="inline-block p-4 border-b-2 border-blue-600 text-blue-600 dark:text-blue-400 rounded-t-lg"
                    onClick={() => window.document.getElementById('gajah4-section')?.scrollIntoView({behavior: 'smooth'})}
                  >
                    Kos Gajah 4
                  </button>
                </li>
                <li className="mr-2">
                  <button 
                    className="inline-block p-4 border-b-2 border-transparent hover:border-blue-300 hover:text-blue-400 rounded-t-lg"
                    onClick={() => window.document.getElementById('gajah3-section')?.scrollIntoView({behavior: 'smooth'})}
                  >
                    Kos Gajah 3
                  </button>
                </li>
                <li className="mr-2">
                  <button 
                    className="inline-block p-4 border-b-2 border-transparent hover:border-blue-300 hover:text-blue-400 rounded-t-lg"
                    onClick={() => window.document.getElementById('anduring-section')?.scrollIntoView({behavior: 'smooth'})}
                  >
                    Kos Anduring
                  </button>
                </li>
                <li>
                  <button 
                    className="inline-block p-4 border-b-2 border-transparent hover:border-blue-300 hover:text-blue-400 rounded-t-lg"
                    onClick={() => window.document.getElementById('limau-manis-section')?.scrollIntoView({behavior: 'smooth'})}
                  >
                    Kos Limau Manis
                  </button>
                </li>
              </ul>
            </div>
            
            {/* Building sections with IDs for navigation */}
            <div id="gajah4-section">
              {renderResidentTable(gajah4Residents, "Akun Kos Gajah 4")}
            </div>
            
            <div id="gajah3-section">
              {renderResidentTable(gajah3Residents, "Akun Kos Gajah 3")}
            </div>
            
            <div id="anduring-section">
              {renderResidentTable(anduringResidents, "Akun Kos Anduring")}
            </div>
            
            <div id="limau-manis-section">
              {renderResidentTable(limauManisResidents, "Akun Kos Limau Manis")}
            </div>
          </>
        )}

        {/* Refresh Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={loadResidents}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Memuat...' : 'Refresh Data'}
          </button>
        </div>
      </div>
    </div>
  );
}
